"use client";

import useLockScroll from "@/hooks/useLockScroll";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import SmoothImage from "@/components/SmoothImage";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

export type MediaItem = {
  type: string;
  src: string;
  thumb?: string;
  title?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  media: MediaItem[];
  initialIndex?: number;
};

function formatGalleryVideoUrl(url: string = ""): {
  formattedUrl: string;
  isDirect: boolean;
} {
  if (!url) return { formattedUrl: "", isDirect: false };
  let cleanUrl = url.trim();

  const isDirect =
    cleanUrl.endsWith(".mp4") ||
    cleanUrl.endsWith(".webm") ||
    cleanUrl.endsWith(".ogg");

  if (isDirect) return { formattedUrl: cleanUrl, isDirect: true };

  let videoId = "";
  if (cleanUrl.includes("youtube.com/watch?v=")) {
    videoId = cleanUrl.split("v=")[1]?.split("&")[0] || "";
  } else if (cleanUrl.includes("youtu.be/")) {
    videoId = cleanUrl.split("youtu.be/")[1]?.split("?")[0] || "";
  } else if (cleanUrl.includes("youtube.com/embed/")) {
    videoId = cleanUrl.split("youtube.com/embed/")[1]?.split("?")[0] || "";
  }

  if (videoId) {
    return {
      formattedUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1&loop=1&playlist=${videoId}&playsinline=1&enablejsapi=1`,
      isDirect: false,
    };
  }

  return { formattedUrl: cleanUrl, isDirect: false };
}

export default function GalleryModal({
  open,
  onClose,
  media,
  initialIndex = 0,
}: Props) {
  const [index, setIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (open) {
      setIndex(initialIndex);
      setZoom(1);
      setIsLoading(true);
    }
  }, [open, initialIndex]);

  useEffect(() => {
    setIsLoading(true);
  }, [index]);
  const [mounted, setMounted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const dragLimit = 500 * zoom;
  const current = media[index];

  const next = () => setIndex((i) => (i + 1) % media.length);
  const prev = () => setIndex((i) => (i - 1 + media.length) % media.length);
  // full screen toggle

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  };
  /* ---------------- zoom controls ---------------- */

  const zoomIn = () => setZoom((z) => Math.min(z + 0.3, 4));

  const zoomOut = () =>
    setZoom((z) => {
      const next = Math.max(z - 0.3, 1);
      return next;
    });

  /* scroll wheel zoom with strict page scroll lock */
  useEffect(() => {
    if (!open || !mounted) return;

    const handleWheelNative = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();

      setZoom((z) => {
        if (e.deltaY > 0) return Math.max(z - 0.2, 1);
        return Math.min(z + 0.2, 4);
      });
    };

    window.addEventListener("wheel", handleWheelNative, { passive: false });
    return () => {
      window.removeEventListener("wheel", handleWheelNative);
    };
  }, [open, mounted]);

  /* ---------------- image change ---------------- */

  const changeImage = (i: number) => {
    setIndex(i);
    setZoom(1);
  };

  /* ---------------- lock page scroll ---------------- */
  useLockScroll(open);

  /* ---------------- mount fix for next SSR ---------------- */

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const nextItem = media[index + 1];
    if (!nextItem || nextItem.type !== "image") return;

    const img = new window.Image();
    img.src = nextItem.src;
  }, [index]);
  /* ---------------- keyboard navigation ---------------- */

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();

      if (e.key === "ArrowRight") next();

      if (e.key === "ArrowLeft") prev();

      if (e.key === "+") zoomIn();

      if (e.key === "-") zoomOut();
    };

    window.addEventListener("keydown", handler);

    return () => window.removeEventListener("keydown", handler);
  }, []);

  if (!open || !mounted) return null;

  const modalContent = (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-modal bg-black/75 flex flex-col backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Title at the top left */}
        {current?.title && (
          <div className="absolute left-6 top-6 z-20 text-white text-xs md:text-sm font-extrabold uppercase tracking-wide bg-black/60 backdrop-blur-md px-4 py-2.5 rounded-lg border border-white/10 max-w-[calc(100%-240px)] truncate shadow-lg">
            {current.title}
          </div>
        )}

        <div className="absolute right-6 top-6 flex items-center gap-3 z-20">
          <button
            onClick={zoomIn}
            className="w-10 h-10 bg-black/60 text-white rounded flex items-center justify-center hover:bg-black"
          >
            +
          </button>

          <button
            onClick={zoomOut}
            className="w-10 h-10 bg-black/60 text-white rounded flex items-center justify-center hover:bg-black"
          >
            −
          </button>

          <button
            onClick={toggleFullscreen}
            className="w-10 h-10 bg-black/60 text-white rounded flex items-center justify-center hover:bg-black"
          >
            ⛶
          </button>

          <button
            onClick={onClose}
            className="w-10 h-10 bg-black/60 text-white rounded flex items-center justify-center hover:bg-black"
          >
            ✕
          </button>
        </div>

        {/* main preview */}

        <div className="flex-1 flex items-center justify-center overflow-hidden relative">
          {/* Loading spinner for high-res images */}
          {isLoading && current?.type === "image" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center z-10 pointer-events-none">
              <div className="w-10 h-10 border-3 border-white/20 border-t-white rounded-full animate-spin" />
            </div>
          )}

          {current.type === "image" && (
            <motion.div
              key={`${index}-${zoom === 1 ? "reset" : "zoom"}`}
              drag={zoom > 1}
              dragElastic={0.1}
              dragMomentum={false}
              dragConstraints={{
                left: -dragLimit,
                right: dragLimit,
                top: -dragLimit,
                bottom: dragLimit,
              }}
              whileTap={{ cursor: "grabbing" }}
              className="cursor-grab touch-none"
              style={{ scale: zoom }}
              onDoubleClick={() => setZoom((z) => (z > 1 ? 1 : 2))}
            >
              <SmoothImage
                src={current.src}
                alt={current.title || "Gallery Preview"}
                width={4000}
                height={3000}
                sizes="(max-width: 768px) 100vw, 1600px"
                quality={100}
                priority
                draggable={false}
                className="max-h-[85vh] w-auto max-w-full object-contain pointer-events-none select-none rounded drop-shadow-2xl"
              />
            </motion.div>
          )}

          {current.type === "video" &&
            (() => {
              const { formattedUrl, isDirect } = formatGalleryVideoUrl(
                current.src,
              );
              return (
                <div className="relative z-10 w-full max-w-4xl aspect-video bg-black rounded-lg overflow-hidden flex items-center justify-center">
                  {isDirect ? (
                    <video
                      ref={(el) => {
                        if (el) {
                          el.volume = 0.5;
                        }
                      }}
                      src={formattedUrl}
                      controls
                      autoPlay
                      loop
                      playsInline
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <iframe
                      src={formattedUrl}
                      title="Video player"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full z-0 border-none"
                    />
                  )}
                </div>
              );
            })()}
        </div>

        {/* thumbnails */}

        <div className="h-[140px] flex items-center gap-4 overflow-x-auto px-10 pb-2">
          {media.map((m, i) => {
            const active = i === index;
            const type = m.type?.toLowerCase();

            if (type !== "image" && type !== "video") return null;

            const RANDOM_THUMBS = [
              "/images/phoi_canh_1.png",
              "/images/phoi_canh_2.png",
              "/images/phoi_canh_3.png",
              "/images/phoi_canh_4.png",
            ];

            const isValidImgSrc = (url?: string) => {
              if (!url) return false;
              if (
                url.includes("youtube.com") ||
                url.includes("youtu.be") ||
                url.endsWith(".mp4")
              ) {
                return false;
              }
              return true;
            };

            const thumbSrc = isValidImgSrc(m.thumb)
              ? m.thumb!
              : isValidImgSrc(m.src)
                ? m.src
                : RANDOM_THUMBS[i % RANDOM_THUMBS.length];

            return (
              <div
                key={i}
                onClick={() => changeImage(i)}
                className={`
                relative w-[160px] h-[100px] shrink-0 cursor-pointer
                border-2 transition
                ${active ? "border-white scale-105" : "border-transparent"}
                `}
              >
                {type === "image" && (
                  <SmoothImage
                    src={thumbSrc}
                    alt=""
                    fill
                    sizes="160px"
                    quality={20}
                    className="object-cover"
                  />
                )}

                {type === "video" && (
                  <>
                    <SmoothImage
                      src={thumbSrc}
                      alt=""
                      fill
                      sizes="160px"
                      quality={20}
                      className="object-cover"
                    />

                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-black/70 flex items-center justify-center text-white text-lg">
                        ▶
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
}
